import { User, SigninRedirectArgs, SignoutRedirectArgs } from "oidc-client-ts";
import { useContext, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import {
	AttributeType,
	IKeyCloakGroup,
	IKeyCloakUser,
	IUseKeycloak,
} from "./Entities";
import { KeycloakAuthContext } from "./KeycloakProvider";
import {
	Delete,
	Fetch,
	FetchBlob,
	FetchJson,
	FetchList,
	FiltersType,
	IProtectedUrl,
	PaginatePostList,
	PaginateFetchList,
	Post,
	Put,
	PostList,
	DownloadFile,
} from "./RestHelpers";

export function useKeycloak(): IUseKeycloak {
	const { data } = useContext(KeycloakAuthContext);
	const { hello: url, realm, backend } = data.configuration;
	const auth = useAuth();

	const adminUrl: string = `${url}/admin/realms/${realm}`;
	const usersUrl = `${adminUrl}/users`;
	const groupsUrl = `${adminUrl}/groups`;

	const authenticatedUser: User | null | undefined = auth?.user;

	useEffect(() => {
		// the `return` is important - addAccessTokenExpiring() returns a cleanup function
		return auth.events.addAccessTokenExpiring(() => {
			auth.signinSilent();
		});
	}, [auth.events, auth.signinSilent]);

	function createURL(path: string): URL {
		var baseurl = !backend || backend == "" ? window.location.origin : backend;
		var url = new URL(baseurl);
		url.pathname = path;
		return url;
	}

	async function login(args?: SigninRedirectArgs): Promise<boolean> {
		try {
			await auth?.signinRedirect({ redirectTarget: "self", ...args });
		} catch (err) {
			console.error(err);
			return false;
		}
		return true;
	}

	async function logout(args?: SignoutRedirectArgs): Promise<boolean> {
		try {
			await auth?.signoutRedirect({
				redirectTarget: "self",
				post_logout_redirect_uri: window.location.origin,
				...args,
			});
			await auth?.removeUser();
		} catch (err) {
			console.error(err);
			return false;
		}
		return true;
	}

	async function getUsers(): Promise<IKeyCloakUser[]> {
		var protectedUrl: IProtectedUrl = {
			url: usersUrl,
			token: auth.user?.access_token,
		};

		return await FetchList<IKeyCloakUser>(protectedUrl);
	}

	async function getUser(userId: string): Promise<IKeyCloakUser | null> {
		if (!userId) return null;

		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${userId}`,
			token: auth.user?.access_token,
		};

		return await FetchJson<IKeyCloakUser>(protectedUrl);
	}

	async function deleteUser(userId: string): Promise<string> {
		if (!userId) return "Missing userId";

		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${userId}`,
			token: auth.user?.access_token,
		};

		return await Delete<IKeyCloakUser>(protectedUrl);
	}

	async function setUserEnabled(
		userId: string,
		isEnabled: boolean | undefined,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${userId}`,
			token: auth.user?.access_token,
		};

		return await Put<any>(protectedUrl, { enabled: isEnabled });
	}

	async function updateAttribute(
		user: IKeyCloakUser,
		attributes: AttributeType,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${user.id}`,
			token: auth.user?.access_token,
		};
		return await Put<any>(protectedUrl, { attributes: attributes });
	}

	async function removeAttribute(
		user: IKeyCloakUser,
		attributeKey: string,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${user.id}`,
			token: auth.user?.access_token,
		};
		if (user?.attributes[attributeKey]) {
			delete user?.attributes[attributeKey];
			return await Put<any>(protectedUrl, { attributes: user?.attributes });
		}

		return "";
	}

	async function getGroups(
		bribriefRepresentation: boolean = true,
	): Promise<IKeyCloakGroup[]> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/?briefRepresentation=${bribriefRepresentation}`,
			token: auth.user?.access_token,
		};

		return await FetchList<IKeyCloakGroup>(protectedUrl);
	}

	async function findGroupsByName(
		name: string,
		bribriefRepresentation: boolean = true,
	): Promise<IKeyCloakGroup[]> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/?search=${name}&briefRepresentation=${bribriefRepresentation}`,
			token: auth.user?.access_token,
		};

		return await FetchList<IKeyCloakGroup>(protectedUrl);
	}

	async function findGroupsByAttribute(
		attribute: string,
		value: string,
		bribriefRepresentation: boolean = true,
	): Promise<IKeyCloakGroup[]> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/?q=${attribute}:${value}&briefRepresentation=${bribriefRepresentation}`,
			token: auth.user?.access_token,
		};

		return await FetchList<IKeyCloakGroup>(protectedUrl);
	}

	async function createGroup(
		name: string,
		attributes?: AttributeType,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: groupsUrl,
			token: auth.user?.access_token,
		};

		return await Post(protectedUrl, { name: name, attributes: attributes });
	}

	async function getGroup(groupId: string): Promise<IKeyCloakGroup | null> {
		if (!groupId) return null;

		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/${groupId}`,
			token: auth.user?.access_token,
		};

		return await FetchJson<IKeyCloakGroup>(protectedUrl);
	}

	async function setGroup(group: IKeyCloakGroup): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/${group.id}`,
			token: auth.user?.access_token,
		};

		return await Put<IKeyCloakGroup>(protectedUrl, group);
	}

	async function deleteGroup(groupId: string): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/${groupId}`,
			token: auth.user?.access_token,
		};

		return await Delete(protectedUrl);
	}

	async function assignUserToGroup(
		userId: string,
		groupId: string,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${userId}/groups/${groupId}`,
			token: auth.user?.access_token,
		};

		return await Put(protectedUrl);
	}

	async function removeUserFromGroup(
		userId: string,
		groupId: string,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${userId}/groups/${groupId}`,
			token: auth.user?.access_token,
		};

		return await Delete(protectedUrl);
	}

	async function getMembersOfGroup(groupId: string): Promise<IKeyCloakUser[]> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/${groupId}/members`,
			token: auth.user?.access_token,
		};
		return await FetchList<IKeyCloakUser>(protectedUrl);
	}

	async function getUserGroups(
		userId: string,
		bribriefRepresentation: boolean = true,
	): Promise<IKeyCloakGroup[]> {
		var protectedUrl: IProtectedUrl = {
			url: `${usersUrl}/${userId}/groups/?briefRepresentation=${bribriefRepresentation}`,
			token: auth.user?.access_token,
		};
		return await FetchList<IKeyCloakGroup>(protectedUrl);
	}

	async function updateGroupAttribute(
		group: IKeyCloakGroup,
		attributes: AttributeType,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/${group.id}`,
			token: auth.user?.access_token,
		};
		var body: IKeyCloakGroup = { name: group.name, attributes: attributes };
		console.log(JSON.stringify(body), url);
		return await Put<any>(protectedUrl, body);
	}

	async function removeGroupAttribute(
		group: IKeyCloakGroup,
		attributeKey: string,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: `${groupsUrl}/${group.id}`,
			token: auth.user?.access_token,
		};
		var body: IKeyCloakGroup = {
			name: group.name,
			attributes: group.attributes,
		};
		if (body?.attributes && body?.attributes[attributeKey]) {
			delete body.attributes[attributeKey];
			return await Put<any>(protectedUrl, body);
		}
		return "";
	}
	async function paginateFetch<T>(
		url: string | URL,
		filters?: FiltersType,
	): Promise<T[]> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await PaginateFetchList<T>(protectedUrl, filters);
	}

	async function paginatePost<T>(
		url: string | URL,
		filters?: FiltersType,
	): Promise<T[]> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await PaginatePostList<T>(protectedUrl, filters);
	}

	async function post<T>(url: string | URL, body?: T): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await Post<T>(protectedUrl, body);
	}

	async function postList<T, R>(url: string, body?: T): Promise<R[]> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await PostList<T, R>(protectedUrl, body);
	}

	async function fetch<T>(url: string | URL): Promise<T | null> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await Fetch<T>(protectedUrl);
	}

	async function fetchBlob<T>(url: string | URL): Promise<T | null> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await FetchBlob<T>(protectedUrl);
	}

	async function fetchList<T>(url: string | URL): Promise<T[]> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await FetchList<T>(protectedUrl);
	}

	async function fetchJson<T>(url: string | URL): Promise<T | null> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await FetchJson<T>(protectedUrl);
	}

	async function put<T>(url: string | URL, body?: T): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await Put<T>(protectedUrl, body);
	}

	async function downloadFile(
		url: string | URL,
		filename: string,
	): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await DownloadFile(protectedUrl, filename);
	}

	async function deleteItem<T>(url: string | URL, body?: T): Promise<string> {
		var protectedUrl: IProtectedUrl = {
			url: getURL(url),
			token: auth.user?.access_token,
		};

		return await Delete<T>(protectedUrl, body);
	}

	const isAuthenticated: boolean = auth?.isAuthenticated ?? false;
	const isConfigurated: boolean = data.configuration.hello.length > 0;

	return {
		login,
		logout,
		createURL,
		getUsers,
		createGroup,
		getGroups,
		getGroup,
		setGroup,
		findGroupsByName,
		findGroupsByAttribute,
		deleteGroup,
		deleteUserFromGroup: removeUserFromGroup,
		getUser,
		getUserGroups,
		assignUserToGroup,
		getMembersOfGroup,
		deleteUser,
		setUserEnabled,
		updateAttribute,
		removeAttribute,
		updateGroupAttribute,
		removeGroupAttribute,
		isAuthenticated,
		isConfigurated,
		authenticatedUser,
		fetch,
		fetchJson,
		fetchBlob,
		fetchList,
		paginateFetch,
		paginatePost,
		post,
		postList,
		put,
		deleteItem,
		downloadFile,
	};
}
function getURL(url: string | URL): string {
	return typeof url === "string" ? url : url.toJSON();
}
